import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { IndianRupee, Percent, Wallet } from "lucide-react";
import { Figure, money, when } from "@/components/PartnerProducts";

/**
 * The doctor's commission on the patients they send — read the same way as their
 * product share, but from its own ledger. The two are never added together, and the
 * breakdown names the items that were left out because product share already paid
 * for them.
 */
export const PartnerCommission = () => {
  const [data, setData] = useState({ rate_pct: 0, totals: {}, orders: [] });

  useEffect(() => {
    api.get("/partner/commission").then(({ data: body }) => setData(body)).catch(() => {});
  }, []);

  const totals = data.totals || {};

  return (
    <Card className="border-border" data-testid="partner-commission">
      <CardContent className="p-6 lg:p-8">
        <h2 className="font-display text-2xl mb-1">Referral commission earnings</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {data.rate_pct
            ? `You earn ${data.rate_pct}% on the patients you send, once their order is paid.`
            : "Your rate is still being agreed with the clinic, so nothing has been earned yet."}
          {" "}Items you own yourself are left out here — those are paid through your product share.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Figure name="This month" value={money(totals.this_month)} icon={Percent}
                  testid="commission-this-month" />
          <Figure name="Lifetime accrued" value={money(totals.lifetime)} icon={IndianRupee}
                  testid="commission-lifetime" />
          <Figure name="Paid to date" value={money(totals.paid)} icon={Wallet}
                  testid="commission-paid" />
          <Figure name="Pending payout" value={money(totals.pending)} icon={IndianRupee}
                  testid="commission-pending" />
        </div>

        {(data.orders || []).length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="commission-orders-empty">
            No commission-earning orders yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="commission-orders-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-normal">Paid</th>
                  <th className="py-2 pr-4 font-normal">Patient</th>
                  <th className="py-2 pr-4 font-normal">Eligible items</th>
                  <th className="py-2 pr-4 font-normal">Rate</th>
                  <th className="py-2 pr-4 font-normal">You earned</th>
                  <th className="py-2 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((row) => (
                  <tr key={row.id} className="border-b border-border/60" data-testid={`commission-order-${row.id}`}>
                    <td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">{when(row.sold_at)}</td>
                    <td className="py-2.5 pr-4">{row.customer_name || "—"}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      {money(row.base_amount)}
                      {row.excluded_amount > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {money(row.excluded_amount)} on your product share
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-4">{row.rate_pct}%</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">{money(row.amount)}</td>
                    <td className="py-2.5">
                      <Badge variant="outline" className={`text-[10px] capitalize ${
                        row.status === "reversed"
                          ? "border-destructive/40 text-destructive bg-destructive/10"
                          : "border-primary/40 text-primary bg-primary/10"}`}>
                        {row.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PartnerCommission;
